import { z } from 'zod';

// ─── care-appointment prompt-context projection ─────────────────────────────
//
// The bounded row shape the migration-context upcoming-appointment signal reads
// from `care_appointments`. Only the columns the signal needs are projected —
// the start instant plus the status used to keep a row eligible — so the read
// path never touches the clinician, attendance, or provenance columns. The row
// shape is hand-authored, not mirrored from the Drizzle table; readers parse the
// stored projection through THIS schema (ADR-088 Rule 3), never an `as`-cast. Two
// drift guards in the sibling test keep it honest: a `rowResponse` key-alignment
// check ties the projection back to `care_appointments` (ADR-066 Rule 3), and the
// canonical fixture breaks visibly on any shape change (ADR-088 Rule 4).

/**
 * A `care_appointments` row as the migration-context reader projects it: the
 * appointment's start instant and its stored status. `status` is carried only
 * to keep the eligibility filter honest at parse time — the signal renders
 * presence + date, never the clinician, exact time, or any other column.
 */
export const upcomingAppointmentRowSchema = z.object({
  startsAt: z.coerce.date(),
  status: z.string(),
});
export type UpcomingAppointmentRow = z.infer<typeof upcomingAppointmentRowSchema>;

/**
 * A canonical stored `care_appointments` row as the migration-context reader
 * sees it — a booked appointment at a fixed future instant so the presence +
 * date rendering is deterministic in tests. Readers consume this builder, never
 * hand-rolled row literals, so a shape change breaks one fixture visibly
 * (ADR-088 Rule 4).
 */
export function canonicalUpcomingAppointmentRow(
  overrides: Partial<UpcomingAppointmentRow> = {},
): UpcomingAppointmentRow {
  return {
    startsAt: new Date('2026-09-25T15:00:00.000Z'),
    status: 'booked',
    ...overrides,
  };
}
